/**
 * ParticleSystem.js — GPU-simulated particle pools.
 * owner: fx
 *
 * All particle state lives in floating-point textures and is advanced by a
 * fullscreen shader. The CPU never touches a particle after it is spawned; it
 * only injects new ones (one small Points draw) and reads back nothing.
 *
 * Per pool, per frame:
 *   1 update pass  (MRT: position/age, velocity/life, seed/size/type/rotation)
 *   1 injection pass (only when something spawned)
 *   0..N bitonic sort passes (alpha pools only, amortised across frames)
 *   1 instanced draw
 *
 * Simulation features: gravity, exponential drag, curl-noise turbulence,
 * size-over-life and colour-over-life curves, rotation, and screen-space
 * depth-buffer collision with restitution and friction.
 *
 * Shading features: soft-particle depth fade, sun+ambient lighting with a
 * density-gradient self-shadow approximation, dissolve-over-life erosion,
 * velocity-aligned stretching, per-particle size/tint jitter.
 */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { NOISE, CURL, DEPTH_HELPERS } from './Shaders.js';

const TEX_W = 128;

/* ------------------------------------------------------------------ *
 * shared GLSL
 * ------------------------------------------------------------------ */
const IDX = /* glsl */`
uniform vec2 uTexSize;
ivec2 idx2(int i){ int w = int(uTexSize.x); return ivec2(i % w, i / w); }
int idx1(ivec2 c){ return c.y * int(uTexSize.x) + c.x; }
`;

const PARAM_FETCH = /* glsl */`
uniform sampler2D tParams;
uniform float uTypeCount;
vec4 param(float type, int col){ return texelFetch(tParams, ivec2(col, int(type)), 0); }
`;

/* ---------------------------- update ---------------------------- */
const UPDATE_FS = /* glsl */`
precision highp float;
precision highp sampler2D;
layout(location = 0) out vec4 oPos;
layout(location = 1) out vec4 oVel;
layout(location = 2) out vec4 oMisc;

uniform sampler2D tPos, tVel, tMisc;
uniform float uDt, uTime, uHasDepth;
uniform mat4 uProj, uView;
uniform mat3 uViewInvRot;
uniform vec3 uFloor;          // x = enabled, y = plane height, z = restitution
${NOISE}
${CURL}
${IDX}
${PARAM_FETCH}
${DEPTH_HELPERS}

void main(){
  ivec2 tc = ivec2(gl_FragCoord.xy);
  vec4 P = texelFetch(tPos,  tc, 0);
  vec4 V = texelFetch(tVel,  tc, 0);
  vec4 M = texelFetch(tMisc, tc, 0);

  float life = V.w;
  float age  = P.w;
  if (life <= 0.0){ oPos = P; oVel = V; oMisc = M; return; }

  age += uDt;
  if (age >= life){ oPos = vec4(P.xyz, life); oVel = vec4(V.xyz, 0.0); oMisc = M; return; }

  float type = M.z;
  vec4 p0 = param(type, 0);   // gravity, drag, turbulence, turbFreq
  vec4 p1 = param(type, 1);   // stretch, rotSpeed, light, bounce
  vec4 p3 = param(type, 3);   // sizeJitter, tintJitter, collide, -

  vec3 pos = P.xyz;
  vec3 vel = V.xyz;
  float seed = M.x;

  vel.y -= p0.x * uDt;
  vel *= exp(-p0.y * uDt);
  if (p0.z > 0.0){
    vel += curlNoise(pos + seed * 13.0, p0.w, uTime) * p0.z * uDt * 7.0;
  }

  vec3 next = pos + vel * uDt;

  /* ---- screen-space depth collision ---- */
  if (p3.z > 0.5 && uHasDepth > 0.5){
    vec4 vp = uView * vec4(next, 1.0);
    vec4 clip = uProj * vp;
    if (clip.w > 0.001){
      vec2 uv = clip.xy / clip.w * 0.5 + 0.5;
      if (uv.x > 0.0 && uv.x < 1.0 && uv.y > 0.0 && uv.y < 1.0){
        float sceneZ = fxSceneZ(uv);
        float pz = -vp.z;
        float thickness = 0.45 + M.y * 4.0;
        if (pz > sceneZ && pz < sceneZ + thickness){
          vec3 nw = normalize(uViewInvRot * fxSceneN(uv));
          float vn = dot(vel, nw);
          if (vn < 0.0){
            vec3 vt = vel - vn * nw;
            vel = vt * 0.62 - vn * nw * p1.w;      // friction + restitution
            next = pos + vel * uDt + nw * 0.006;
            if (dot(vel, vel) < 0.09) vel *= 0.35; // settle instead of jitter
          } else {
            next = pos + nw * 0.004;
          }
        }
      }
    }
  }

  /* ---- world floor fallback (off-screen geometry has no depth) ---- */
  if (p3.z > 0.5 && uFloor.x > 0.5 && next.y < uFloor.y){
    next.y = uFloor.y;
    if (vel.y < 0.0){
      vel.y = -vel.y * uFloor.z;
      vel.xz *= 0.62;
      if (abs(vel.y) < 0.25) vel *= 0.3;
    }
  }

  float rot = M.w + p1.y * (seed * 2.0 - 1.0) * uDt;

  oPos  = vec4(next, age);
  oVel  = vec4(vel, life);
  oMisc = vec4(M.xyz, rot);
}
`;

/* ---------------------------- injection ---------------------------- */
const INJECT_VS = /* glsl */`
uniform vec2 uTexSize;
attribute vec3 aVel;
attribute vec4 aData;    // slot, life, seed, size
attribute vec4 aData2;   // type, rot, age, -
out vec3 vPos;
out vec3 vVel;
out vec4 vData;
out vec4 vData2;
void main(){
  float slot = aData.x;
  float x = mod(slot, uTexSize.x);
  float y = floor(slot / uTexSize.x);
  vec2 uv = (vec2(x, y) + 0.5) / uTexSize;
  gl_Position = vec4(uv * 2.0 - 1.0, 0.0, 1.0);
  gl_PointSize = 1.0;
  vPos = position; vVel = aVel; vData = aData; vData2 = aData2;
}
`;

const INJECT_FS = /* glsl */`
precision highp float;
layout(location = 0) out vec4 oPos;
layout(location = 1) out vec4 oVel;
layout(location = 2) out vec4 oMisc;
in vec3 vPos;
in vec3 vVel;
in vec4 vData;
in vec4 vData2;
void main(){
  oPos  = vec4(vPos, vData2.z);
  oVel  = vec4(vVel, vData.y);
  oMisc = vec4(vData.z, vData.w, vData2.x, vData2.y);
}
`;

/* ---------------------------- sorting ---------------------------- */
const SORT_KEY_FS = /* glsl */`
precision highp float;
uniform sampler2D tSort, tPos, tVel;
uniform vec3 uCamPos;
uniform float uInit;
${IDX}
out vec4 fragColor;
void main(){
  ivec2 tc = ivec2(gl_FragCoord.xy);
  float index = uInit > 0.5 ? float(idx1(tc)) : texelFetch(tSort, tc, 0).x;
  ivec2 pc = idx2(int(index));
  vec4 P = texelFetch(tPos, pc, 0);
  vec4 V = texelFetch(tVel, pc, 0);
  bool alive = V.w > 0.0 && P.w < V.w;
  float key = alive ? -distance(P.xyz, uCamPos) : 1.0e8;
  fragColor = vec4(index, key, 0.0, 0.0);
}
`;

const SORT_STEP_FS = /* glsl */`
precision highp float;
uniform sampler2D tSort;
uniform float uK, uJ;
${IDX}
out vec4 fragColor;
void main(){
  ivec2 tc = ivec2(gl_FragCoord.xy);
  int i = idx1(tc);
  int j = int(uJ);
  int k = int(uK);
  int partner = i ^ j;
  vec4 self  = texelFetch(tSort, tc, 0);
  vec4 other = texelFetch(tSort, idx2(partner), 0);
  bool up = ((i & k) == 0);
  bool lower = partner > i;
  bool takeMin = (up == lower);
  bool selfSmaller = self.y <= other.y;
  fragColor = (takeMin == selfSmaller) ? self : other;
}
`;

/* ---------------------------- render ---------------------------- */
const RENDER_VS = /* glsl */`
precision highp float;
precision highp sampler2D;
uniform sampler2D tPos, tVel, tMisc, tSort, tGrad, tCurve;
uniform mat4 uProj, uView;
uniform vec2 uResolution;
uniform float uSizeScale;
${IDX}
${PARAM_FETCH}

attribute float aId;

varying vec2 vUv;
varying vec4 vColor;
varying float vT;
varying float vViewZ;
varying float vSprite;
varying vec4 vShade;     // light, emissive, soft, dissolve
varying vec3 vRight;
varying vec3 vUp;
varying vec3 vViewPos;
varying float vSeed;
varying float vWiden;
varying float vMedia;

void main(){
  int id = int(aId);
  #ifdef SORTED
    id = int(texelFetch(tSort, idx2(id), 0).x);
  #endif
  ivec2 tc = idx2(id);
  vec4 P = texelFetch(tPos,  tc, 0);
  vec4 V = texelFetch(tVel,  tc, 0);
  vec4 M = texelFetch(tMisc, tc, 0);

  float life = V.w;
  float t = life > 0.0 ? P.w / life : 2.0;
  if (t >= 1.0 || life <= 0.0){ gl_Position = vec4(0.0, 0.0, -3.0, 1.0); return; }

  float type = M.z;
  vec4 p1 = param(type, 1);
  vec4 p2 = param(type, 2);
  vec4 p3 = param(type, 3);
  float row = (type + 0.5) / uTypeCount;

  float sizeMul = texture2D(tCurve, vec2(t, row)).x;
  float seed = M.x;
  float jit = 1.0 + (seed - 0.5) * 2.0 * p3.x * 0.5;
  float size = M.y * sizeMul * jit * uSizeScale;

  vec4 col = texture2D(tGrad, vec2(t, row));
  float tj = 1.0 + (fract(seed * 71.13) - 0.5) * 2.0 * p3.y;
  col.rgb *= tj;

  vec4 mv = uView * vec4(P.xyz, 1.0);

  vec3 right = vec3(1.0, 0.0, 0.0);
  vec3 up    = vec3(0.0, 1.0, 0.0);
  vec2 corner = position.xy;

  if (p1.x > 0.001){
    vec3 vv = (uView * vec4(V.xyz, 0.0)).xyz;
    float len = length(vv.xy);
    if (len > 0.02){
      vec2 d = vv.xy / len;
      up = vec3(d, 0.0);
      right = vec3(d.y, -d.x, 0.0);
      float speed = length(V.xyz);
      corner.y *= 1.0 + min(speed * p1.x * 0.11, 22.0);
    }
  } else {
    float c = cos(M.w), s = sin(M.w);
    corner = mat2(c, -s, s, c) * corner;
  }

  /* Sub-pixel particles alias away to nothing — a spark thinner than a pixel
     just flickers. Widen anything below the minimum screen width and hand the
     energy back through alpha, so total brightness is conserved. */
  float pxPerMetre = uResolution.y * uProj[1][1] * 0.5 / max(0.05, -mv.z);
  float widen = max(1.0, 2.1 / max(size * pxPerMetre, 1e-3));
  if (p1.x > 0.001) corner.x *= widen; else corner *= widen;
  vWiden = 1.0 / widen;

  vec3 vpos = mv.xyz + right * corner.x * size + up * corner.y * size;
  gl_Position = uProj * vec4(vpos, 1.0);

  vUv = uv;
  vColor = col;
  vT = t;
  vViewZ = -vpos.z;
  vSprite = p2.w;
  vShade = vec4(p1.z, p2.x, p2.y, p2.z);
  vRight = right;
  vUp = up;
  vViewPos = vpos;
  vSeed = seed;
  vMedia = p3.w;
}
`;

const RENDER_FS = /* glsl */`
precision highp float;
precision highp sampler2D;
uniform sampler2D tAtlas, tBlue;
uniform vec2 uResolution;
uniform float uHasDepth, uAtlasTexel, uOpacity;
uniform vec3 uSunDirView, uSunColor, uAmbSky, uAmbGround, uUpView;
${DEPTH_HELPERS}

varying vec2 vUv;
varying vec4 vColor;
varying float vT;
varying float vViewZ;
varying float vSprite;
varying vec4 vShade;
varying vec3 vRight;
varying vec3 vUp;
varying vec3 vViewPos;
varying float vSeed;
varying float vWiden;
varying float vMedia;

layout(location = 0) out vec4 pc_fragColor;

vec2 atlasUV(vec2 uv){
  vec2 tile = vec2(mod(vSprite, 4.0), floor(vSprite / 4.0));
  return (tile + clamp(uv, 0.0, 1.0)) * 0.25;
}

/** Density (atlas R channel) at a TILE-space uv. Clamped inside our own tile, so
 *  a long march toward the light can never bleed into the neighbouring sprite. */
float tileDensity(vec2 uv){ return texture2D(tAtlas, atlasUV(uv)).r; }

void main(){
  vec2 auv = atlasUV(vUv);
  vec4 s = texture2D(tAtlas, auv);

  float cov = s.a;
  float dissolve = vShade.w;
  if (dissolve > 0.0){
    float thr = vT * dissolve;
    cov *= smoothstep(thr, thr + 0.62, s.g * 0.8 + 0.42);
  }

  float alpha = cov * vColor.a * uOpacity * vWiden;
  if (alpha <= 0.003) discard;

  vec3 rgb = vColor.rgb;

  /* ---- lighting ----
   *
   * Two models, chosen per particle type by the media flag:
   *
   *   media = 0  a small solid (chip, splinter, shard, leaf, droplet). It has a
   *              surface, so it gets a wrap-diffuse surface response.
   *
   *   media = 1  participating media (smoke, dust, mist). It has no surface, and
   *              shading it as one is what makes CG smoke read as a lit disc.
   *              Light is attenuated by the optical depth it had to push through
   *              to REACH this texel, marched from here toward the sun across a
   *              real fraction of the sprite, and redirected toward the eye by a
   *              Henyey-Greenstein phase function.
   *
   *              The march distance is the whole point. Sampling one or two
   *              texels away collapses the integral to exp(-k * localDensity):
   *              radially symmetric, dark in the dense centre, full brightness on
   *              the thin rim — a bright-rimmed donut, every puff identical. Over
   *              a real span the same integral becomes a LATERAL gradient: lit on
   *              the side facing the sun, shadowed on the far side, which is what
   *              a ball of smoke actually does.
   *
   *              multi is the multiply-scattered fill: photons that bounce
   *              several times inside the puff and leak out of the shadowed side.
   *              Without it a dense core goes to soot black; with it the core is
   *              grey and dense, which is what smoke looks like.
   */
  float lightAmt = vShade.x;
  if (lightAmt > 0.0){
    float h  = s.r;                        // local density, ~1 at the core
    float tx = uAtlasTexel * 4.0;          // one atlas texel, in TILE units
    float hx = tileDensity(vUv + vec2(tx, 0.0));
    float hy = tileDensity(vUv + vec2(0.0, tx));
    vec3 nT = normalize(vec3((h - hx) * 14.0, (h - hy) * 14.0, 0.52));
    vec3 nView = normalize(vRight * nT.x + vUp * nT.y + vec3(0.0, 0.0, 1.0) * nT.z);
    float upDot = dot(nView, uUpView) * 0.5 + 0.5;

    vec3 lit;
    if (vMedia > 0.001){
      /* ---- optical depth from this texel toward the sun ---- */
      vec2 lp = vec2(dot(uSunDirView, vRight), dot(uSunDirView, vUp));
      float ll = length(lp);
      lp = ll > 1e-4 ? lp / ll : vec2(0.0, 1.0);
      float tau = h * 0.22;
      tau += tileDensity(vUv + lp * 0.075) * 0.30;
      tau += tileDensity(vUv + lp * 0.165) * 0.26;
      tau += tileDensity(vUv + lp * 0.290) * 0.14;
      tau += tileDensity(vUv + lp * 0.440) * 0.08;
      // vMedia is the medium's optical density, not a flag: a smoke column at 1.0
      // is genuinely opaque and self-shadows hard, a bullet-impact dust puff at
      // 0.5 is a thin powder and shading it like a smoke column turns it into a
      // dark smudge on a sunlit wall.
      tau *= 2.9 * vMedia;
      float trans = exp(-tau);
      float multi = exp(-tau * 0.26) * 0.26;

      /* ---- Henyey-Greenstein, forward biased ---- */
      vec3 viewDir = normalize(-vViewPos);          // particle -> camera
      float cosT = dot(-uSunDirView, viewDir);      // +1 = sun directly behind
      const float g = 0.55;
      float den = 1.0 + g * g - 2.0 * g * cosT;
      float hg = (1.0 - g * g) / max(1e-4, den * sqrt(den));
      // The isotropic floor matters as much as the forward lobe. Too low and a
      // side-lit powder comes out darker than the surface it was blasted off,
      // which is physically wrong: fine dust has a high albedo and scatters in
      // every direction, and a sunlit puff reads BRIGHTER than the concrete.
      float phase = 0.52 + min(hg, 7.0) * 0.26;

      // the sky reaches the outside of the puff; the core sees much less of it
      vec3 amb = mix(uAmbGround, uAmbSky, upDot) * mix(1.0, 0.40, clamp(h, 0.0, 1.0));
      lit = uSunColor * (phase * trans + multi) + amb;

      // Thin media scatters less toward the eye: opacity has to follow density,
      // or the sprite boundary reads as a hard-edged blob and the low-density
      // rim keeps the brightness of a full column.
      alpha *= clamp(0.45 + 0.70 * h, 0.0, 1.0);
    } else {
      // A chip of concrete is a surface, but it is also 5 mm across and tumbling:
      // a full Lambert response makes a shower of debris read as a shower of
      // white confetti. Wrap it a little and keep it under the surface it came off.
      float ndl = dot(nView, uSunDirView);
      float wrapd = clamp((ndl + 0.22) / 1.22, 0.0, 1.0);
      lit = uSunColor * wrapd * 0.70 + mix(uAmbGround, uAmbSky, upDot);
    }
    rgb *= lit * lightAmt;
  }

  /* ---- hot core (sparks, embers, fire) ---- */
  float em = vShade.y;
  if (em > 0.0) rgb *= 1.0 + s.b * 2.2 * em;

  /* ---- soft particles ---- */
  if (uHasDepth > 0.5){
    float sceneZ = fxSceneZ(gl_FragCoord.xy / uResolution);
    float soft = max(vShade.z, 0.02);
    alpha *= clamp((sceneZ - vViewZ) / soft, 0.0, 1.0);
  }
  // fade out as a particle reaches the near plane so nothing pops in the face
  alpha *= clamp((vViewZ - 0.08) / 0.22, 0.0, 1.0);
  if (alpha <= 0.003) discard;

  // dither the alpha to kill banding on big soft puffs
  float bn = texture2D(tBlue, gl_FragCoord.xy / 256.0).r;
  alpha = max(0.0, alpha + (bn - 0.5) * 0.012);

  vec4 outColor = vec4(rgb, alpha);
  #if defined( TONE_MAPPING )
    outColor.rgb = toneMapping(outColor.rgb);
  #endif
  outColor = linearToOutputTexel(outColor);
  pc_fragColor = outColor;
}
`;

/* ------------------------------------------------------------------ *
 * pool
 * ------------------------------------------------------------------ */
export class ParticlePool {
  /**
   * @param {THREE.WebGLRenderer} renderer
   * @param {object} opts { size, sorted, additive, name, maxSpawnPerFrame, sortPassesPerFrame }
   */
  constructor(renderer, res, opts = {}) {
    this.renderer = renderer;
    this.res = res;                       // { textures, types }
    this.name = opts.name || 'pool';
    this.size = opts.size || 8192;
    this.sorted = !!opts.sorted;
    this.additive = !!opts.additive;
    this.maxSpawn = opts.maxSpawnPerFrame || 2048;
    this.sortPassesPerFrame = opts.sortPassesPerFrame ?? 6;

    const w = TEX_W;
    const h = Math.max(1, Math.ceil(this.size / w));
    this.size = w * h;
    this.texW = w;
    this.texH = h;
    this.cursor = 0;
    this.spawnCount = 0;
    this.aliveEstimate = 0;
    this.drawCalls = 0;

    const rtOpts = {
      count: 3,
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false,
      generateMipmaps: false,
      colorSpace: THREE.NoColorSpace,
    };
    this.rtA = new THREE.WebGLRenderTarget(w, h, rtOpts);
    this.rtB = new THREE.WebGLRenderTarget(w, h, rtOpts);

    this._buildUpdate();
    this._buildInject();
    if (this.sorted) this._buildSort();
    this._buildRender();
    this._clearState();
  }

  get texSize() { return new THREE.Vector2(this.texW, this.texH); }

  _buildUpdate() {
    this.updateUniforms = {
      tPos: { value: null }, tVel: { value: null }, tMisc: { value: null },
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      tCurl: { value: this.res.textures.curl },
      tParams: { value: this.res.types.params },
      uTypeCount: { value: this.res.types.count },
      uDt: { value: 0 }, uTime: { value: 0 },
      uHasDepth: { value: 0 },
      uProj: { value: new THREE.Matrix4() },
      uView: { value: new THREE.Matrix4() },
      uViewInvRot: { value: new THREE.Matrix3() },
      uTexSize: { value: this.texSize },
      uFloor: { value: new THREE.Vector3(1, 0, 0.25) },
    };
    this.updateQuad = new FullScreenQuad(new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: this.updateUniforms,
      vertexShader: 'void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: UPDATE_FS,
      depthTest: false, depthWrite: false,
    }));
  }

  _buildInject() {
    const n = this.maxSpawn;
    const g = new THREE.BufferGeometry();
    this._sPos = new Float32Array(n * 3);
    this._sVel = new Float32Array(n * 3);
    this._sData = new Float32Array(n * 4);
    this._sData2 = new Float32Array(n * 4);
    g.setAttribute('position', new THREE.BufferAttribute(this._sPos, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aVel', new THREE.BufferAttribute(this._sVel, 3).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aData', new THREE.BufferAttribute(this._sData, 4).setUsage(THREE.DynamicDrawUsage));
    g.setAttribute('aData2', new THREE.BufferAttribute(this._sData2, 4).setUsage(THREE.DynamicDrawUsage));
    g.setDrawRange(0, 0);
    this._injectGeo = g;

    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: { uTexSize: { value: this.texSize } },
      vertexShader: INJECT_VS,
      fragmentShader: INJECT_FS,
      depthTest: false, depthWrite: false, blending: THREE.NoBlending,
    });
    this._injectPoints = new THREE.Points(g, mat);
    this._injectPoints.frustumCulled = false;
    this._injectScene = new THREE.Scene();
    this._injectScene.add(this._injectPoints);
    this._injectCam = new THREE.Camera();
  }

  _buildSort() {
    const rtOpts = {
      type: THREE.FloatType, format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
      colorSpace: THREE.NoColorSpace,
    };
    this.sortA = new THREE.WebGLRenderTarget(this.texW, this.texH, rtOpts);
    this.sortB = new THREE.WebGLRenderTarget(this.texW, this.texH, rtOpts);

    this.keyUniforms = {
      tSort: { value: null }, tPos: { value: null }, tVel: { value: null },
      uCamPos: { value: new THREE.Vector3() }, uInit: { value: 1 },
      uTexSize: { value: this.texSize },
    };
    this.keyQuad = new FullScreenQuad(new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: this.keyUniforms,
      vertexShader: 'void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: SORT_KEY_FS,
      depthTest: false, depthWrite: false,
    }));

    this.stepUniforms = {
      tSort: { value: null }, uK: { value: 2 }, uJ: { value: 1 },
      uTexSize: { value: this.texSize },
    };
    this.stepQuad = new FullScreenQuad(new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: this.stepUniforms,
      vertexShader: 'void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: SORT_STEP_FS,
      depthTest: false, depthWrite: false,
    }));

    this.schedule = [];
    for (let k = 2; k <= this.size; k <<= 1) {
      for (let j = k >> 1; j > 0; j >>= 1) this.schedule.push([k, j]);
    }
    this.schedulePos = this.schedule.length; // force a key build on the first frame
    this.sortInitialised = false;
  }

  _buildRender() {
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([
      -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0,
    ], 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    g.setIndex([0, 1, 2, 0, 2, 3]);
    const ids = new Float32Array(this.size);
    for (let i = 0; i < this.size; i++) ids[i] = i;
    g.setAttribute('aId', new THREE.InstancedBufferAttribute(ids, 1));
    g.instanceCount = this.size;
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);

    this.renderUniforms = {
      tPos: { value: null }, tVel: { value: null }, tMisc: { value: null },
      tSort: { value: null }, tGrad: { value: this.res.types.gradient },
      tCurve: { value: this.res.types.curve }, tParams: { value: this.res.types.params },
      tAtlas: { value: this.res.textures.sprites },
      tDepth: { value: null }, tNormal: { value: null }, uDepthMode: { value: 0 },
      tBlue: { value: this.res.textures.blueNoise },
      uTypeCount: { value: this.res.types.count },
      uTexSize: { value: this.texSize },
      uProj: { value: new THREE.Matrix4() },
      uView: { value: new THREE.Matrix4() },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uHasDepth: { value: 0 },
      uAtlasTexel: { value: 1 / 2048 },
      uSizeScale: { value: 1 }, uOpacity: { value: 1 },
      uSunDirView: { value: new THREE.Vector3(0, 1, 0) },
      uSunColor: { value: new THREE.Color(1, 1, 1) },
      uAmbSky: { value: new THREE.Color(0.2, 0.25, 0.32) },
      uAmbGround: { value: new THREE.Color(0.1, 0.09, 0.08) },
      uUpView: { value: new THREE.Vector3(0, 1, 0) },
    };

    const mat = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      uniforms: this.renderUniforms,
      vertexShader: RENDER_VS,
      fragmentShader: RENDER_FS,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      blending: this.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      side: THREE.DoubleSide,
      defines: this.sorted ? { SORTED: '' } : {},
    });
    this.mesh = new THREE.Mesh(g, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = this.additive ? 3010 : 3000;
    this.mesh.name = `fx_particles_${this.name}`;
    this.mesh.userData.noCollide = true;
    this.mesh.userData.fx = true;
  }

  _clearState() {
    // one injection of "dead" particles across the whole buffer
    const r = this.renderer;
    const prev = r.getRenderTarget();
    const prevAuto = r.autoClear;
    r.autoClear = false;
    const clearQuad = new FullScreenQuad(new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: 'void main(){ gl_Position = vec4(position.xy, 0.0, 1.0); }',
      fragmentShader: `precision highp float;
        layout(location = 0) out vec4 a; layout(location = 1) out vec4 b; layout(location = 2) out vec4 c;
        void main(){ a = vec4(0.0); b = vec4(0.0); c = vec4(0.0); }`,
      depthTest: false, depthWrite: false,
    }));
    for (const rt of [this.rtA, this.rtB]) {
      r.setRenderTarget(rt);
      clearQuad.render(r);
    }
    r.setRenderTarget(prev);
    r.autoClear = prevAuto;
    clearQuad.material.dispose();
    clearQuad.dispose();
    this.cursor = 0;
    if (this.sorted) { this.sortInitialised = false; this.schedulePos = this.schedule.length; }
  }

  reset() { this._clearState(); this.spawnCount = 0; this.aliveEstimate = 0; }

  /** Inject one particle. Returns false when this frame's staging buffer is full. */
  spawn(px, py, pz, vx, vy, vz, life, size, type, seed, rot = 0, age = 0) {
    const i = this.spawnCount;
    if (i >= this.maxSpawn) return false;
    const slot = this.cursor;
    this.cursor = (this.cursor + 1) % this.size;
    this._sPos[i * 3] = px; this._sPos[i * 3 + 1] = py; this._sPos[i * 3 + 2] = pz;
    this._sVel[i * 3] = vx; this._sVel[i * 3 + 1] = vy; this._sVel[i * 3 + 2] = vz;
    this._sData[i * 4] = slot; this._sData[i * 4 + 1] = life;
    this._sData[i * 4 + 2] = seed; this._sData[i * 4 + 3] = size;
    this._sData2[i * 4] = type; this._sData2[i * 4 + 1] = rot;
    this._sData2[i * 4 + 2] = age; this._sData2[i * 4 + 3] = 0;
    this.spawnCount++;
    return true;
  }

  /** Advance one frame. `env` carries camera/depth/lighting state. */
  update(dt, env) {
    const r = this.renderer;
    this.drawCalls = 0;
    const prevTarget = r.getRenderTarget();
    const prevAuto = r.autoClear;
    r.autoClear = false;

    /* ---- simulate: A -> B ---- */
    const u = this.updateUniforms;
    u.tPos.value = this.rtA.textures[0];
    u.tVel.value = this.rtA.textures[1];
    u.tMisc.value = this.rtA.textures[2];
    u.tDepth.value = env.depthTexture;
    u.tNormal.value = env.normalTexture || env.depthTexture;
    u.uDepthMode.value = env.depthMode;
    u.uHasDepth.value = env.depthTexture ? 1 : 0;
    u.uDt.value = Math.min(dt, 0.05);
    u.uTime.value = env.time;
    u.uProj.value.copy(env.camera.projectionMatrix);
    u.uView.value.copy(env.camera.matrixWorldInverse);
    u.uViewInvRot.value.setFromMatrix4(env.camera.matrixWorld);
    u.uFloor.value.copy(env.floor);
    r.setRenderTarget(this.rtB);
    this.updateQuad.render(r);
    this.drawCalls++;

    /* ---- inject new particles into B ---- */
    if (this.spawnCount > 0) {
      const g = this._injectGeo;
      g.attributes.position.addUpdateRange(0, this.spawnCount * 3);
      g.attributes.aVel.addUpdateRange(0, this.spawnCount * 3);
      g.attributes.aData.addUpdateRange(0, this.spawnCount * 4);
      g.attributes.aData2.addUpdateRange(0, this.spawnCount * 4);
      g.attributes.position.needsUpdate = true;
      g.attributes.aVel.needsUpdate = true;
      g.attributes.aData.needsUpdate = true;
      g.attributes.aData2.needsUpdate = true;
      g.setDrawRange(0, this.spawnCount);
      r.setRenderTarget(this.rtB);
      r.render(this._injectScene, this._injectCam);
      this.drawCalls++;
      this.aliveEstimate += this.spawnCount;
      this.spawnCount = 0;
    }

    const t = this.rtA; this.rtA = this.rtB; this.rtB = t;

    /* ---- amortised bitonic sort ---- */
    if (this.sorted) this._sortStep(env);

    r.setRenderTarget(prevTarget);
    r.autoClear = prevAuto;

    /* ---- bind for drawing ---- */
    const ru = this.renderUniforms;
    ru.tPos.value = this.rtA.textures[0];
    ru.tVel.value = this.rtA.textures[1];
    ru.tMisc.value = this.rtA.textures[2];
    ru.tSort.value = this.sorted ? this.sortA.texture : null;
    ru.tDepth.value = env.depthTexture;
    ru.tNormal.value = env.normalTexture || env.depthTexture;
    ru.uDepthMode.value = env.depthMode;
    ru.uHasDepth.value = env.depthTexture ? 1 : 0;
    ru.uProj.value.copy(env.camera.projectionMatrix);
    ru.uView.value.copy(env.camera.matrixWorldInverse);
    ru.uResolution.value.copy(env.resolution);
    ru.uSunDirView.value.copy(env.sunDirView);
    ru.uSunColor.value.copy(env.sunColor);
    ru.uAmbSky.value.copy(env.ambSky);
    ru.uAmbGround.value.copy(env.ambGround);
    ru.uUpView.value.copy(env.upView);
    ru.uAtlasTexel.value = env.atlasTexel;
    this.drawCalls++; // the instanced draw itself
  }

  _sortStep(env) {
    const r = this.renderer;
    let budget = this.sortPassesPerFrame;
    if (budget <= 0) return;

    if (this.schedulePos >= this.schedule.length) {
      // refresh keys (keeping the existing permutation) and restart the network
      this.keyUniforms.tSort.value = this.sortA.texture;
      this.keyUniforms.tPos.value = this.rtA.textures[0];
      this.keyUniforms.tVel.value = this.rtA.textures[1];
      this.keyUniforms.uCamPos.value.copy(env.camera.position);
      this.keyUniforms.uInit.value = this.sortInitialised ? 0 : 1;
      r.setRenderTarget(this.sortB);
      this.keyQuad.render(r);
      const t = this.sortA; this.sortA = this.sortB; this.sortB = t;
      this.sortInitialised = true;
      this.schedulePos = 0;
      this.drawCalls++;
      budget--;
    }

    while (budget > 0 && this.schedulePos < this.schedule.length) {
      const [k, j] = this.schedule[this.schedulePos++];
      this.stepUniforms.tSort.value = this.sortA.texture;
      this.stepUniforms.uK.value = k;
      this.stepUniforms.uJ.value = j;
      r.setRenderTarget(this.sortB);
      this.stepQuad.render(r);
      const t = this.sortA; this.sortA = this.sortB; this.sortB = t;
      this.drawCalls++;
      budget--;
    }
  }

  dispose() {
    this.rtA.dispose(); this.rtB.dispose();
    this.updateQuad.material.dispose(); this.updateQuad.dispose();
    this._injectGeo.dispose(); this._injectPoints.material.dispose();
    this.mesh.geometry.dispose(); this.mesh.material.dispose();
    if (this.sorted) {
      this.sortA.dispose(); this.sortB.dispose();
      this.keyQuad.material.dispose(); this.keyQuad.dispose();
      this.stepQuad.material.dispose(); this.stepQuad.dispose();
    }
  }
}
